import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import { CHART_FONT_SIZE, cartesianFrame } from "@/lib/tool-kit/charts/layout";
import { linePath, type Point } from "@/lib/tool-kit/charts/lines";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import {
  curvePoints,
  type Inflection,
  type LogisticParams,
} from "@/lib/tool-kit/ecology/logistic";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const WIDTH = 640;
const HEIGHT = 380;

export interface LogisticChartLabels {
  xTitle: string;
  yTitle: string;
  capacity: string;
  inflection: string;
  current: string;
  title: string;
  description: string;
}

/**
 * N(t) drawn from 0 to `xMax` with the carrying capacity as a dashed line, the
 * inflection (K ÷ 2) as a hollow circle, and the chosen time as a diamond.
 */
export function LogisticChart({
  id,
  params,
  xMax,
  now,
  inflection,
  fmt,
  labels,
}: {
  id: string;
  params: LogisticParams;
  xMax: number;
  now: { t: number; n: number } | null;
  inflection: Inflection;
  fmt: Format;
  labels: LogisticChartLabels;
}) {
  const yAxis = niceTicks(0, Math.max(params.k, params.n0) * 1.04, {
    includeZero: true,
  });
  const xAxis = niceTicks(0, xMax, { exact: true });
  const xEnd = Math.max(xAxis.ticks.at(-1) ?? xMax, xMax);
  const format = (value: number) => fmt(value, { maximumSignificantDigits: 4 });
  const frame = cartesianFrame({
    width: WIDTH,
    height: HEIGHT,
    xTitle: labels.xTitle,
    yTitle: labels.yTitle,
    leftLabelWidth: maxTextWidth(yAxis.ticks.map(format), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    rightPadding: 14,
  });
  const { plot } = frame;
  const x = linearScale([0, xEnd], [plot.x, plot.x + plot.width]);
  const y = linearScale([yAxis.min, yAxis.max], [plot.y + plot.height, plot.y]);
  const points: Point[] = curvePoints(params, xEnd, 80).map(
    ({ t, n }) => [x(t), y(n)] as const
  );
  const capacityY = y(params.k);
  const inflectionInRange = inflection.time !== null && inflection.time <= xEnd;
  const inflectionX = x(inflection.time ?? 0);
  return (
    <ChartSvg
      description={labels.description}
      height={HEIGHT}
      id={id}
      label={labels.title}
      width={WIDTH}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        format={format}
        orient="left"
        plot={plot}
        scale={y}
        ticks={yAxis.ticks}
      />
      <ValueAxis
        format={format}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={xAxis.ticks}
      />
      <line
        stroke={CHART_INK.muted}
        strokeDasharray="6 4"
        strokeWidth={1.5}
        x1={px(plot.x)}
        x2={px(plot.x + plot.width)}
        y1={px(capacityY)}
        y2={px(capacityY)}
      />
      <text
        fill={CHART_INK.muted}
        fontSize={CHART_FONT_SIZE.tick}
        textAnchor="end"
        x={px(plot.x + plot.width - 4)}
        y={px(capacityY - 6)}
      >
        {labels.capacity}
      </text>
      <path
        d={linePath(points)}
        fill="none"
        stroke={CHART_COLORS[0]}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2.5}
      />
      {inflectionInRange ? (
        <g>
          <Marker
            fill={CHART_COLORS[3]}
            hollow
            shape="circle"
            size={11}
            x={inflectionX}
            y={y(inflection.size)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={CHART_FONT_SIZE.tick}
            textAnchor="start"
            x={px(inflectionX + 10)}
            y={px(y(inflection.size) + 16)}
          >
            {labels.inflection}
          </text>
        </g>
      ) : null}
      {now ? (
        <g>
          <Marker
            fill={CHART_COLORS[1]}
            shape="diamond"
            size={11}
            x={x(now.t)}
            y={y(now.n)}
          />
          <text
            fill={CHART_INK.text}
            fontSize={CHART_FONT_SIZE.tick}
            textAnchor="end"
            x={px(x(now.t) - 9)}
            y={px(y(now.n) - 8)}
          >
            {labels.current}
          </text>
        </g>
      ) : null}
    </ChartSvg>
  );
}
